// ============================================================================
// AudioWorklet Processor
// 遅延削減のポイント：
// 1. AudioWorkletを使用（ScriptProcessorNodeより低遅延）
// 2. 100msバッファで効率とリアルタイム性のバランスを取る
// 3. リングバッファでデータロスを防ぐ
// ============================================================================

export const WORKLET_NAME = 'audio-recorder-worklet';

/**
 * AudioWorkletProcessor のコード
 *
 * パフォーマンス最適化ポイント：
 * - Float32Arrayを直接操作（メモリコピー最小化）
 * - リングバッファ方式でオーバーヘッド削減
 * - 128サンプルフレーム単位の処理（Web Audio API標準）
 */
export const audioWorkletCode = `
// ============================================================================
// AudioWorklet Processor - 低遅延リアルタイム音声処理
// ============================================================================

class AudioRecorderProcessor extends AudioWorkletProcessor {
  constructor(options) {
    super();

    // バッファ設定（100ms相当）
    // sampleRate * 0.1 = 100ms分のサンプル数
    this.targetDurationMs = options?.processorOptions?.bufferDurationMs || 100;
    this.bufferSize = Math.floor(sampleRate * (this.targetDurationMs / 1000));

    // リングバッファ初期化
    this._buffer = new Float32Array(this.bufferSize);
    this._writeIndex = 0;

    // デバッグ用
    this._sampleCount = 0;
    this._lastFlushTime = currentTime;
  }

  /**
   * 音声処理メインループ
   * Web Audio APIから128サンプル単位で呼び出される
   *
   * @returns {boolean} true: 処理継続, false: 処理停止
   */
  process(inputs, outputs, parameters) {
    const input = inputs[0];

    // 入力チェック
    if (!input || !input.length || !input[0]) {
      return true; // 入力なしでも継続
    }

    const channelData = input[0]; // モノラル（最初のチャンネル）
    const inputLength = channelData.length; // 通常128サンプル

    // バッファへ書き込み
    const spaceRemaining = this.bufferSize - this._writeIndex;

    if (inputLength <= spaceRemaining) {
      // バッファに収まる場合：直接コピー
      this._buffer.set(channelData, this._writeIndex);
      this._writeIndex += inputLength;
    } else {
      // バッファを超える場合：分割処理
      // 1. 残りスペースを埋める
      this._buffer.set(channelData.subarray(0, spaceRemaining), this._writeIndex);

      // 2. バッファをフラッシュ
      this._flushBuffer();

      // 3. 残りデータを新バッファに書き込み
      const remaining = channelData.subarray(spaceRemaining);
      this._buffer.set(remaining, 0);
      this._writeIndex = remaining.length;
    }

    // バッファが満杯になったらフラッシュ
    if (this._writeIndex >= this.bufferSize) {
      this._flushBuffer();
    }

    return true; // 処理継続
  }

  /**
   * バッファをメインスレッドに送信
   *
   * 最適化ポイント：
   * - slice()で新しいArrayBufferを作成（SharedArrayBufferは使わない）
   * - Transferable Objectsとして送信可能（メモリコピー削減）
   */
  _flushBuffer() {
    if (this._writeIndex === 0) return;

    // 実際に書き込まれた部分だけを送信
    const dataToSend = this._buffer.slice(0, this._writeIndex);

    // メインスレッドに送信（Transferableとして）
    // 注意：transferはpostMessageの第2引数
    this.port.postMessage(dataToSend, [dataToSend.buffer]);

    // バッファリセット
    this._writeIndex = 0;
    this._sampleCount += dataToSend.length;
  }
}

// プロセッサを登録
registerProcessor('${WORKLET_NAME}', AudioRecorderProcessor);
`;

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * AudioWorkletのBlobURLを生成
 *
 * なぜBlobURLを使うか：
 * - 外部ファイルが不要（バンドルに含められる）
 * - CORSの問題を回避
 * - デプロイが簡単
 *
 * 毎回新規生成する理由：
 * - 再録音時に新しいAudioContextが作成されるが、キャッシュされたBlobURLを
 *   再利用するとブラウザの内部モジュール追跡と衝突し
 *   "Unable to load a worklet's module" エラーが発生する
 * - 数KBのWorkletコードなのでメモリ影響は無視できる
 * - 使用後はURL.revokeObjectURL()で解放すること
 */
export const getAudioWorkletBlobUrl = (): string => {
  const blob = new Blob([audioWorkletCode], { type: 'application/javascript' });
  return URL.createObjectURL(blob);
};

/**
 * Float32Array を Int16Array に変換
 *
 * なぜ変換するか：
 * - 帯域幅削減（4bytes -> 2bytes per sample = 50%削減）
 * - 多くの音声認識APIがPCM16を期待
 *
 * 変換式：
 * - Float32: -1.0 ~ 1.0
 * - Int16:   -32768 ~ 32767
 */
export const float32ToInt16 = (float32Array: Float32Array): Int16Array => {
  const int16Array = new Int16Array(float32Array.length);

  for (let i = 0; i < float32Array.length; i++) {
    // クランプ処理
    const sample = Math.max(-1, Math.min(1, float32Array[i]));
    // 変換
    int16Array[i] = sample < 0
      ? Math.floor(sample * 0x8000) // 負の値
      : Math.floor(sample * 0x7FFF); // 正の値
  }

  return int16Array;
};

/**
 * PCM16データをWAVフォーマットに変換
 * デバッグ用や録音保存用
 */
export const int16ToWav = (
  int16Array: Int16Array,
  sampleRate: number = 16000
): Blob => {
  const numChannels = 1;
  const bytesPerSample = 2;
  const blockAlign = numChannels * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = int16Array.length * bytesPerSample;
  const headerSize = 44;
  const totalSize = headerSize + dataSize;

  const buffer = new ArrayBuffer(totalSize);
  const view = new DataView(buffer);

  // RIFF Header
  writeString(view, 0, 'RIFF');
  view.setUint32(4, totalSize - 8, true);
  writeString(view, 8, 'WAVE');

  // fmt Chunk
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true); // chunk size
  view.setUint16(20, 1, true);  // PCM format
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bytesPerSample * 8, true);

  // data Chunk
  writeString(view, 36, 'data');
  view.setUint32(40, dataSize, true);

  // Audio Data
  const dataView = new Int16Array(buffer, headerSize);
  dataView.set(int16Array);

  return new Blob([buffer], { type: 'audio/wav' });
};

const writeString = (view: DataView, offset: number, str: string): void => {
  for (let i = 0; i < str.length; i++) {
    view.setUint8(offset + i, str.charCodeAt(i));
  }
};

// ============================================================================
// Browser Compatibility Check
// ============================================================================

export interface AudioSupport {
  audioContext: boolean;
  audioWorklet: boolean;
  getUserMedia: boolean;
}

/**
 * ブラウザの音声API対応状況をチェック
 */
export const checkAudioSupport = (): AudioSupport => {
  const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;

  return {
    audioContext: !!AudioContextClass,
    audioWorklet: !!AudioContextClass && 'audioWorklet' in AudioContextClass.prototype,
    getUserMedia: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
  };
};

/**
 * すべての必要機能がサポートされているか
 */
export const isFullySupported = (): boolean => {
  const support = checkAudioSupport();
  return support.audioContext &&
         support.audioWorklet &&
         support.getUserMedia;
};
